import React, {useState} from 'react';
import {useFormik} from 'formik';
import {useNavigate} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  DatePicker, LoadingOverlay,
} from '@finpay-development/shared-components';
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  MenuItem,
  TextField as MuiTextField,
} from '@mui/material';
import {AttachMoney as AttachMoneyIcon} from '@mui/icons-material';
import {Moment} from 'moment';
import {Utils} from '../../shared/utils';
import {RootState} from '../../shared/state/root-reducer';
import {AppDispatch} from '../../shared/state/store';
import {ClientStatusCardViewModel} from '../../shared/model/client-status-card';
import {showErrorStatus} from '../../security/state/user-slice';
import '../../estimates/tab-styling.scss';
import {
  patientService,
} from '../../patient/services/patient-service';
import {
  SearchPatientResultsModal,
  SelectedPatient,
} from './search-patient-results-modal';
import {ApiBatchLimits} from '../../shared/enums';
import {
  admissionsAdvisorService,
} from '../../admissions-advisor/services/admissions-advisor-services';
import {
  payorRiskNameLookup,
  timingRiskNameLookup,
} from '../../shared/model/timing-and-payor-risk';
import _ from 'lodash';
import {RolePageNames} from '../../security/model/role-page-names';
import AccessControl from '../../security/components/access-control';

interface NewPatientFormValues {
  firstName: string;
  lastName: string;
  dateOfBirth: Date | null;
  accountNumber: string;
  email: string;
  pfrAmt?: number;
  clientId?: number;
  facilityId?: number;
}

const baseValidationSchema = Yup.object({
  clientId: Yup.number().required('Client is required'),
  facilityId: Yup.number().required('Facility is required'),
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  dateOfBirth: Yup.date().
      nullable().
      required('Date of birth is required').
      max(new Date(), 'Date of birth cannot be in the future'),
  accountNumber: Yup.string().optional(),
  email: Yup.string().email('Email Format is Invalid').optional(),
  pfrAmt: Yup.number().optional(),
});

const programValidationSchema = baseValidationSchema.shape({
  pfrAmt: Yup.number().required('PFR is required'),
});

function NewPatientView() {

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const clients = useSelector((state: RootState) =>
      state.implementationContext.implementationSpecialistClient.filteredByIsActive,
  ) as ClientStatusCardViewModel[];
  const allClientsMap = useSelector((state: RootState) =>
      state.implementationContext.implementationSpecialistClient.allClientsMap,
  );
  const isLoadingClients = useSelector((state: RootState) =>
      state.implementationContext.implementationSpecialistClient.isLoading,
  );

  const [patientSearchModal, setPatientSearchModal] = useState<{
    searchResult: Record<string, any>[];
    actionType: 'pay' | 'program';
  } | null>(null);

  const [isFormProcessing, setIsFormProcessing] = useState(false)

  const initialValues: NewPatientFormValues = {
    clientId: undefined,
    facilityId: undefined,
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    accountNumber: '',
    email: '',
    pfrAmt: undefined,
  };

  const formik = useFormik({
    initialValues,
    validateOnChange: false,
    validateOnBlur: true,
    validate: async (values: NewPatientFormValues) => {
      const errors: any = {};
      try {
        await baseValidationSchema.validate(values, {abortEarly: false});
      } catch (err: any) {
        err.inner.forEach((error: any) => {
          errors[error.path] = error.message;
        });
      }
      return errors;
    },
    onSubmit: async () => {
    },
  });

  const getHubURL = (
      patientId: number, patientEncounterId: number,
      actionType: 'pay' | 'program') => {
    return `/hub/patient/${patientId}/ioc/${patientEncounterId}/${actionType}`;
  };

  const handleNewPatientActionOnClick = async (actionType: 'pay' | 'program') => {

    try{
      setIsFormProcessing(true)

      const schema = actionType === 'program'
          ? programValidationSchema
          : baseValidationSchema;

      try {
        await schema.validate(formik.values, {abortEarly: false});
      } catch (err: any) {
        let message = 'Please resolve the following issues: ';
        const errors: any = {};
        err.inner.forEach((error: any) => {
          errors[error.path] = error.message;
          message += ` ${error.message};`;
        });
        formik.setTouched(
            Object.keys(formik.values).reduce((acc: any, key) => {
              acc[key] = true;
              return acc;
            }, {}),
            false,
        );
        formik.setErrors(errors);
        dispatch(showErrorStatus(message));
        return;
      }

      const response = await patientService.getAllPatientsWithFilters({
        firstName: formik.values.firstName.trim(),
        lastName: formik.values.lastName.trim(),
        birthDate: formik.values.dateOfBirth?.toISOString().slice(0, 10),
        clientId: formik.values.clientId,
      });

      if (response.length > 0) {
        setPatientSearchModal({
          actionType,
          searchResult: response,
        });

      } else {

        //create patient and ioc

        const patient = await createPatient();

        if(!patient) return

        const patientEncounter = await createIOC(patient.patientId);

        if(!patientEncounter) return

        navigate(getHubURL(patient.patientId, patientEncounter.patientEncounterId,
            actionType));

      }

    }finally{
      setIsFormProcessing(false)
    }

  };

  const createPatient = async () => {

    const response = await patientService.savePatient({
      patientId: 0,
      contact: {
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        birthDate: formik.values.dateOfBirth?.toISOString().slice(0, 10),
        email: formik.values.email,
        smsConsentFl: true
      },
      clientId: formik.values.clientId!.toString(),
    });

    if (response.hasErrors) {
      dispatch(showErrorStatus(
          'Error creating patient. Please reach out to a FinPay support if this problem persists.'));
      return
    }

    return response.entity;

  };

  const createIOC = async (patientId: number) => {

    let riskClassId = 1
    let timingRiskId = 1
    let payorRiskId = 1

    if(formik.values.facilityId && !_.isNil(formik.values.pfrAmt)){

      const riskClassCalcResponse =
          await patientService.getPatientEncounterRiskClass(
              formik.values.facilityId,
              false,
              timingRiskNameLookup(timingRiskId)!,
              payorRiskNameLookup(payorRiskId)!,
              formik.values.pfrAmt
          );

      if (riskClassCalcResponse.hasErrors) {
        dispatch(showErrorStatus(
            'Error calculating risk class. Please reach out to a FinPay support if this problem persists.'));
        return
      }

      riskClassId = riskClassCalcResponse.entity.riskClassId

    }

    const response = await admissionsAdvisorService.createAAPatientIoc({
          patientId: patientId,
          clientId: formik.values.clientId!,
          facilityId: formik.values.facilityId!,
          pfrAmt: formik.values.pfrAmt,
          riskClass: {
            riskClassId,
          },
          timingRisk: {
            timingRiskId,
          },
          payorRisk: {
            payorRiskId,
          },

        },
        0,
    );

    if (response.hasErrors) {
      dispatch(showErrorStatus(
          'Error creating patient instance of care. Please reach out to a FinPay support if this problem persists.'));
      return
    }

    return response.entity;

  };

  const handlePatientSelect = async (
      selectedPatient: SelectedPatient, actionType: 'pay' | 'program') => {

    if (selectedPatient === 'new') {

      //User decided to create new patient, so create new patient and ioc

      const patient = await createPatient();

      if(!patient) return

      const patientEncounter = await createIOC(patient.patientId);

      if(!patientEncounter) return

      navigate(getHubURL(patient.patientId, patientEncounter.patientEncounterId,
          actionType));

      return

    }

    if (selectedPatient?.patientId) {

      const response = await patientService.getPatientInstancesOfCare({
        patientId: selectedPatient.patientId,
        limit: ApiBatchLimits.patientEncounters,
        offset: 0,
      });

      if (response.hasErrors) {
        dispatch(showErrorStatus(
            'Error fetching patient instances of care. Please reach out to a FinPay support if this problem persists.'));
        return;
      }

      //Grabbing the latest unconverted IOC for the facility selected
      const patientEncounters = response.entity.filter(
          (encounters: Record<string, any>) => encounters.workflow.workflowId ===
              2 && encounters.facilityId === formik.values.facilityId).
          sort((a: Record<string, any>,
                b: Record<string, any>) => b.patientEncounterId -
              a.patientEncounterId);

      if (patientEncounters[0]) {
        navigate(getHubURL(selectedPatient.patientId,
            patientEncounters[0].patientEncounterId, actionType));
      } else {
        const patientEncounter = await createIOC(selectedPatient.patientId);
        if(!patientEncounter) return
        navigate(getHubURL(selectedPatient.patientId,
            patientEncounter.patientEncounterId, actionType));
        }
      }

  };

  return (
      <main test-id="new-patient-view">
        <div className="content px-4 py-4">
          <Paper elevation={2} sx={{p: 4, maxWidth: 900, mx: 'auto'}}
                 test-id="new-patient-form-paper">
            {
              isLoadingClients ? (
                  <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 570,
                        width: '100%',
                      }}
                  >
                    <LoadingOverlay whiteBackground={true}/>
                  </Box>
              ) : (
                  <>
                    <Typography variant="h5" component="h1" align="center"
                                gutterBottom fontWeight="bold" sx={{mb: 3}}>
                      Payment Hub
                    </Typography>

                    <form onSubmit={formik.handleSubmit}>
                      <Grid container spacing={2.5}>
                        {/* Client Section */}
                        <Grid item xs={12}>
                          <Box
                              sx={{
                                '& .MuiInputBase-root': {
                                  height: 56,
                                  paddingBottom: '16px',
                                },
                              }}
                          >
                            <MuiTextField
                                select
                                fullWidth
                                error={formik.touched.clientId &&
                                    !!formik.errors.clientId}
                                label="Client"
                                name="clientId"
                                value={formik.values.clientId}
                                onChange={(e) => {
                                  formik.handleChange(e);
                                  formik.setFieldValue('facilityId', undefined, false);
                                }}
                                onBlur={formik.handleBlur}
                                className={`state-field ${formik.touched.clientId &&
                                Boolean(formik.errors.clientId) ?
                                    'error-border' :
                                    'required-fields'}`}
                                inputProps={{'test-id': 'new-patient-client'}}
                                SelectProps={{displayEmpty: true}}
                            >
                              <MenuItem value={undefined}>
                                <Typography>Select Client</Typography>
                              </MenuItem>
                              {clients.map((client) => (
                                  <MenuItem key={client.clientId}
                                            value={client.clientId}>
                                    {client.clientName}
                                  </MenuItem>
                              ))}
                            </MuiTextField>
                          </Box>
                        </Grid>

                        {/* Facility Section */}
                        <Grid item xs={12}>
                          <Box
                              sx={{
                                '& .MuiInputBase-root': {
                                  height: 56,
                                  paddingBottom: '16px',
                                },
                              }}
                          >
                            <MuiTextField
                                select
                                fullWidth
                                error={formik.touched.facilityId &&
                                    !!formik.errors.facilityId}
                                label="Facility"
                                name="facilityId"
                                value={formik.values.facilityId}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`state-field ${formik.touched.facilityId &&
                                Boolean(formik.errors.facilityId) ?
                                    'error-border' :
                                    'required-fields'}`}
                                inputProps={{'test-id': 'new-patient-facility'}}
                                SelectProps={{displayEmpty: true}}
                            >
                              <MenuItem value={undefined}>
                                <Typography>Select Facility</Typography>
                              </MenuItem>
                              {
                                  formik.values.clientId && (
                                      allClientsMap.get(formik.values.clientId)?.
                                          clientFacilities?.
                                          map((facility) => (
                                              <MenuItem key={facility.facilityId}
                                                        value={facility.facilityId}>
                                                {facility.facilityName}
                                              </MenuItem>
                                          ))
                                  )
                              }
                            </MuiTextField>
                          </Box>
                        </Grid>

                        {/* First Name and Last Name Section */}
                        <Grid item xs={12}>
                          <Box sx={{
                            display: 'flex',
                            gap: 2,
                            flexDirection: {xs: 'column', md: 'row'},
                            width: '100%',
                            boxSizing: 'border-box',
                          }}>
                            <Box sx={{
                              flex: '1 1 0',
                              minWidth: 0,
                              '& .MuiInputBase-root': {height: 56},
                            }}>
                              <TextField
                                  label="First Name"
                                  name="firstName"
                                  value={formik.values.firstName}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  className={`state-field ${formik.touched.firstName &&
                                  Boolean(formik.errors.firstName) ?
                                      'error-border' :
                                      'required-fields'}`}
                                  test-id="new-patient-first-name"
                              />
                            </Box>
                            <Box sx={{
                              flex: '1 1 0',
                              minWidth: 0,
                              '& .MuiInputBase-root': {height: 56},
                            }}>
                              <TextField
                                  label="Last Name"
                                  name="lastName"
                                  value={formik.values.lastName}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  className={`state-field ${formik.touched.lastName &&
                                  Boolean(formik.errors.lastName) ?
                                      'error-border' :
                                      'required-fields'}`}
                                  test-id="new-patient-last-name"
                              />
                            </Box>
                          </Box>
                        </Grid>

                        {/* Date of Birth and Email Row */}
                        <Grid item xs={12} sx={{
                          display: 'flex',
                          gap: 2,
                          flexDirection: {xs: 'column', md: 'row'},
                          width: '100%',
                          boxSizing: 'border-box',
                        }}>
                          <Box sx={{
                            flex: '0 0 23.33%',
                            minWidth: 0,
                            '& .MuiInputBase-root': {height: 56},
                          }}>
                            <DatePicker
                                label="Date of Birth"
                                value={formik.values.dateOfBirth}
                                onChange={(date: Moment) => {
                                  const normalized = date?.toDate ?
                                      date.toDate() :
                                      date;
                                  if (Utils.isValidDate(normalized)) {
                                    formik.setFieldValue('dateOfBirth',
                                        normalized);
                                  }
                                }}
                                maxDate={new Date()}
                                className={`date state-field ${formik.touched.dateOfBirth &&
                                Boolean(formik.errors.dateOfBirth) ?
                                    'error-border' :
                                    'required-fields'}`}
                                data="new-patient-date-of-birth"
                            />
                          </Box>
                          <Box sx={{
                            flex: '1 1 0',
                            minWidth: 0,
                            '& .MuiInputBase-root': {height: 56},
                          }}>
                            <TextField
                                label="Email"
                                name="email"
                                type="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                test-id="new-patient-email"
                            />
                          </Box>
                        </Grid>

                        {/* PFR and Account Number Row */}
                        <Grid item xs={12} sx={{
                          display: 'flex',
                          gap: 2,
                          flexDirection: {xs: 'column', md: 'row'},
                          width: '100%',
                          boxSizing: 'border-box',
                        }}>
                          <Box sx={{
                            flex: '0 0 23.33%',
                            minWidth: 0,
                            '& input': {textAlign: 'right'},
                            '& .MuiInputBase-root': {height: 56},
                          }}>
                            <TextField
                                label="PFR"
                                name="pfrAmt"
                                startAdornment={<AttachMoneyIcon/>}
                                value={formik.values.pfrAmt ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const val = e.target.value;
                                  if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                                    formik.handleChange(e);
                                  }
                                }}
                                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                  formik.handleBlur(e);
                                  const num = parseFloat(e.target.value);
                                  if (!isNaN(num) && num >= 0) {
                                    formik.setFieldValue('pfrAmt', Number(num.toFixed(2)), false);
                                  }
                                }}
                                type="text"
                                className={`state-field ${formik.touched.pfrAmt && Boolean(formik.errors.pfrAmt) ? 'error-border' : ''}`}
                                test-id="new-patient-pfr"
                            />
                          </Box>
                          {/*<Box sx={{*/}
                          {/*  flex: '1 1 0',*/}
                          {/*  minWidth: 0,*/}
                          {/*  '& .MuiInputBase-root': {height: 56},*/}
                          {/*}}>*/}
                          {/*  <TextField*/}
                          {/*      error={formik.touched.accountNumber &&*/}
                          {/*          !!formik.errors.accountNumber}*/}
                          {/*      label="Account Number"*/}
                          {/*      name="accountNumber"*/}
                          {/*      value={formik.values.accountNumber}*/}
                          {/*      onChange={formik.handleChange}*/}
                          {/*      onBlur={formik.handleBlur}*/}
                          {/*      helperText={formik.touched.accountNumber ?*/}
                          {/*          formik.errors.accountNumber :*/}
                          {/*          undefined}*/}
                          {/*      test-id="new-patient-account-number"*/}
                          {/*  />*/}
                          {/*</Box>*/}
                        </Grid>

                        {/* Action Buttons */}
                        <Grid item xs={12}>
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            mt: 2,
                          }}>
                            <Box sx={{width: '100%', position: 'relative'}}>
                              <AccessControl
                                  rolePageName={RolePageNames.PaymentHub}
                                  actionName="Take a Payment"
                                  renderNoAccess={() => (
                                      <></>
                                  )}
                              >
                                <Button
                                    type="primary"
                                    onClick={() => handleNewPatientActionOnClick(
                                        'pay')}
                                    test-id="new-patient-take-payment"
                                    style={{
                                      width: '100%',
                                      paddingTop: '1rem',
                                      paddingBottom: '1rem',
                                      fontSize: '2rem',
                                      fontWeight: 600,
                                    }}
                                    disabled={isFormProcessing}
                                >
                                  Take a Payment
                                </Button>
                              </AccessControl>
                              {isFormProcessing && (
                                  <Box sx={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(25, 118, 210, 0.9)',
                                    borderRadius: '4px',
                                  }}>
                                    <CircularProgress size={24} sx={{color: 'white'}}/>
                                  </Box>
                              )}
                            </Box>
                            <Box sx={{width: '100%', position: 'relative'}}>

                              <AccessControl
                                  rolePageName={RolePageNames.PaymentHub}
                                  actionName="Set Up Payment Program"
                                  renderNoAccess={() => (
                                      <></>
                                  )}
                              >
                                <Button
                                    type="primary"
                                    onClick={() => handleNewPatientActionOnClick(
                                        'program')}
                                    test-id="new-patient-set-up-payment-program"
                                    style={{
                                      width: '100%',
                                      paddingTop: '1rem',
                                      paddingBottom: '1rem',
                                      fontSize: '2rem',
                                      fontWeight: 600,
                                    }}
                                    disabled={isFormProcessing}
                                >
                                  Set Up Payment Program
                                </Button>
                              </AccessControl>
                              {isFormProcessing && (
                                  <Box sx={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(25, 118, 210, 0.9)',
                                    borderRadius: '4px',
                                  }}>
                                    <CircularProgress size={24} sx={{color: 'white'}}/>
                                  </Box>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </form>
                  </>
              )
            }
          </Paper>
        </div>
        {
            patientSearchModal && (
                <SearchPatientResultsModal
                    onClose={() => setPatientSearchModal(null)}
                    searchResult={patientSearchModal.searchResult}
                    onPatientSelect={async (selectedPatient: SelectedPatient) => {
                      await handlePatientSelect(selectedPatient,
                          patientSearchModal.actionType);
                      setPatientSearchModal(null);
                    }}
                />
            )
        }
      </main>
  );
}

export default NewPatientView;

